import React, { useMemo, useState } from 'react';
import {
    ButtonBase,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import type { UsageActiveUser } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import MuiRouterLink from 'c-components/MuiRouterLink';
import { formatNumberWhole } from 'c-lib';
import type { TableSort } from './UsageParts';
import { SortHeader, tableHeadSx, TimeAgo, UsageEmpty, UsageSection } from './UsageParts';
import { organisationLink, useFormatDuration, userLink } from './usageLib';

/** Rows shown before "Show all", so the table sits level with the column beside it. */
const SHOWN = 8;

type Column = 'name' | 'organisation' | 'active_days' | 'sessions' | 'time_ms' | 'last_seen';

const valueOf = (user: UsageActiveUser, column: Column): string | number => {
    if (column === 'name') {
        return (user.name ?? '').toLowerCase();
    }
    if (column === 'organisation') {
        return (user.organisation?.name ?? '').toLowerCase();
    }
    return user[column] ?? '';
};

const sortUsers = (users: UsageActiveUser[], sort: TableSort<Column>) => {
    const sign = sort.direction === 'asc' ? 1 : -1;
    return [...users].sort((a, b) => {
        const left = valueOf(a, sort.key);
        const right = valueOf(b, sort.key);
        if (left < right) {
            return -sign;
        }
        if (left > right) {
            return sign;
        }
        return 0;
    });
};

/**
 * Everyone active in the period, admins included. Admins act for no organisation, so the
 * organisation list can never lead to them: this table is how the overview reaches every user.
 */
const UsageActiveUsers = ({ users }: { users: UsageActiveUser[] }) => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const [all, setAll] = useState(false);
    // Most time first, as the API sends them.
    const [sort, setSort] = useState<TableSort<Column>>({ key: 'time_ms', direction: 'desc' });
    const sorted = useMemo(() => sortUsers(users, sort), [users, sort]);
    const shown = all ? sorted : sorted.slice(0, SHOWN);
    const right = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

    return (
        <UsageSection
            title={t('Modules.Admin.Usage.activeUsersTitle')}
            hint={t('Modules.Admin.Usage.activeUsersHint')}
        >
            {users.length === 0 ? (
                <UsageEmpty />
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead sx={tableHeadSx}>
                            <TableRow>
                                <SortHeader column="name" sort={sort} onSort={setSort} first="asc">
                                    {t('Modules.Admin.Usage.columns.person')}
                                </SortHeader>
                                <SortHeader
                                    column="organisation"
                                    sort={sort}
                                    onSort={setSort}
                                    first="asc"
                                >
                                    {t('Modules.Admin.Usage.columns.organisation')}
                                </SortHeader>
                                <SortHeader
                                    column="active_days"
                                    sort={sort}
                                    onSort={setSort}
                                    align="right"
                                >
                                    {t('Modules.Admin.Usage.columns.activeDays')}
                                </SortHeader>
                                <SortHeader
                                    column="sessions"
                                    sort={sort}
                                    onSort={setSort}
                                    align="right"
                                >
                                    {t('Modules.Admin.Usage.columns.sessions')}
                                </SortHeader>
                                <SortHeader
                                    column="time_ms"
                                    sort={sort}
                                    onSort={setSort}
                                    align="right"
                                >
                                    {t('Modules.Admin.Usage.columns.time')}
                                </SortHeader>
                                <SortHeader column="last_seen" sort={sort} onSort={setSort}>
                                    {t('Modules.Admin.Usage.columns.lastSeen')}
                                </SortHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.map(user => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <MuiRouterLink
                                            to={userLink(user.id)}
                                            fontWeight={500}
                                            underline="hover"
                                        >
                                            {user.name ?? `#${user.id}`}
                                        </MuiRouterLink>
                                        {user.role && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                            >
                                                {t(`Modules.Admin.Usage.types.${user.role}`)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {user.organisation ? (
                                            <MuiRouterLink
                                                to={organisationLink(
                                                    user.organisation.type,
                                                    user.organisation.id,
                                                )}
                                                underline="hover"
                                            >
                                                {user.organisation.name ??
                                                    `#${user.organisation.id}`}
                                            </MuiRouterLink>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {t('Modules.Admin.Usage.noOrganisation')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={right}>{user.active_days}</TableCell>
                                    <TableCell sx={right}>
                                        {formatNumberWhole(user.sessions)}
                                    </TableCell>
                                    <TableCell sx={right}>{formatDuration(user.time_ms)}</TableCell>
                                    <TableCell
                                        sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                                    >
                                        <TimeAgo date={user.last_seen} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {users.length > SHOWN && (
                <ButtonBase
                    onClick={() => setAll(open => !open)}
                    aria-expanded={all}
                    sx={{
                        alignSelf: 'flex-start',
                        mt: 'auto',
                        minHeight: 44,
                        color: 'primary.main',
                        fontSize: 14,
                        fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {all
                        ? t('Modules.Admin.Usage.showFewer')
                        : t('Modules.Admin.Usage.showAllPeople', { count: users.length })}
                </ButtonBase>
            )}
        </UsageSection>
    );
};

export default UsageActiveUsers;
